import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useCategoriaCampos } from '@/hooks/useCategoriaCampos';
import { NewCampoCategoriaDialog } from './NewCampoCategoriaDialog';
import { EditCampoCategoriaDialog } from './EditCampoCategoriaDialog';
import { CategoriaCampoCustomizado } from '@/types/categoria-campo';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface CamposCustomizadosManagerProps {
  categoriaId: string;
}

const getTipoCampoLabel = (tipo: string) => {
  const labels: Record<string, string> = {
    text: 'Texto',
    textarea: 'Texto Longo',
    number: 'Número',
    date: 'Data',
    boolean: 'Sim/Não',
    select: 'Seleção',
    multiselect: 'Seleção Múltipla',
    email: 'E-mail',
    url: 'URL',
  };
  return labels[tipo] || tipo;
};

export const CamposCustomizadosManager = ({ categoriaId }: CamposCustomizadosManagerProps) => {
  const { campos, isLoading, deleteCampo } = useCategoriaCampos(categoriaId);
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const [editingCampo, setEditingCampo] = useState<CategoriaCampoCustomizado | null>(null);
  const [deletingCampoId, setDeletingCampoId] = useState<string | null>(null);

  const handleDelete = async () => {
    if (deletingCampoId) {
      await deleteCampo.mutateAsync(deletingCampoId);
      setDeletingCampoId(null);
    }
  };

  if (isLoading) {
    return <div className="text-center py-4">Carregando campos...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Campos Customizados</h3>
        <Button onClick={() => setIsNewDialogOpen(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Adicionar Campo
        </Button>
      </div>

      {campos.length === 0 ? (
        <Card className="p-6 text-center text-muted-foreground">
          Nenhum campo customizado configurado. Clique em "Adicionar Campo" para começar.
        </Card>
      ) : (
        <div className="space-y-2">
          {campos.map((campo) => (
            <Card key={campo.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{campo.label}</h4>
                    {campo.obrigatorio && (
                      <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded">
                        Obrigatório
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    <span className="font-medium">Tipo:</span> {getTipoCampoLabel(campo.tipo_campo)}
                    {campo.descricao && (
                      <>
                        {' • '}
                        <span>{campo.descricao}</span>
                      </>
                    )}
                  </div>
                  {campo.configuracoes?.opcoes && campo.configuracoes.opcoes.length > 0 && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Opções: {campo.configuracoes.opcoes.join(', ')}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingCampo(campo)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeletingCampoId(campo.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <NewCampoCategoriaDialog
        categoriaId={categoriaId}
        isOpen={isNewDialogOpen}
        onClose={() => setIsNewDialogOpen(false)}
      />

      {editingCampo && (
        <EditCampoCategoriaDialog
          campo={editingCampo}
          isOpen={!!editingCampo}
          onClose={() => setEditingCampo(null)}
        />
      )}

      <AlertDialog open={!!deletingCampoId} onOpenChange={() => setDeletingCampoId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover este campo? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
